import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import api from '../lib/api';
import { setFormDirty } from '../lib/updateGuard';
import { ArrowLeft } from '@phosphor-icons/react';

export default function Signup() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => { setFormDirty('signup', true); return () => setFormDirty('signup', false); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !password) return;
    setSubmitting(true);
    try {
      await api.post('/organiser/signup', { name: name.trim(), email: email.trim(), password });
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Signup failed';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#F4F6F5] flex flex-col items-center justify-center px-4" data-testid="signup-success">
        <div className="w-full max-w-md mb-4">
          <Link to="/home" className="inline-flex items-center gap-1.5 text-sm text-[#455561] font-semibold hover:text-[#1B4332] transition-colors" data-testid="back-to-home">
            <ArrowLeft className="w-4 h-4" weight="bold" /> Back to home
          </Link>
        </div>
        <Card className="w-full max-w-md p-8 bg-white border-[#E1E6E4] rounded-xl text-center space-y-4">
          <div className="w-14 h-14 bg-[#E8F5E9] rounded-full flex items-center justify-center mx-auto">
            <svg className="w-7 h-7 text-[#2E7D32]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
          </div>
          <h2 className="text-xl font-bold text-[#1D242B]">Check your inbox</h2>
          <p className="text-sm text-[#455561]">We've sent a verification link to <strong>{email}</strong>. Click it to verify your account.</p>
          <p className="text-xs text-[#B8C7C0] mt-1">Can't see it? Check your Junk or Spam folder, and mark <strong>noreply@foretour.co.uk</strong> as safe.</p>
          <Link to="/login" className="text-sm text-[#1B4332] font-semibold hover:underline">Go to login</Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F6F5] flex flex-col items-center justify-center px-4" data-testid="signup-page">
      <div className="w-full max-w-md mb-4">
        <Link to="/home" className="inline-flex items-center gap-1.5 text-sm text-[#455561] font-semibold hover:text-[#1B4332] transition-colors" data-testid="back-to-home">
          <ArrowLeft className="w-4 h-4" weight="bold" /> Back to home
        </Link>
      </div>
      <Card className="w-full max-w-md p-8 bg-white border-[#E1E6E4] rounded-xl space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#1D242B]">Create an account</h1>
          <p className="text-sm text-[#455561] mt-1">Organise your own golf events on Fore Tour</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-semibold text-[#455561] mb-1 block">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" required data-testid="signup-name" />
          </div>
          <div>
            <label className="text-sm font-semibold text-[#455561] mb-1 block">Email</label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required data-testid="signup-email" />
          </div>
          <div>
            <label className="text-sm font-semibold text-[#455561] mb-1 block">Password</label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" required minLength={8} data-testid="signup-password" />
          </div>
          <Button type="submit" disabled={submitting} className="w-full h-12 bg-[#1B4332] hover:bg-[#163B2B] text-white rounded-xl font-semibold" data-testid="signup-submit">
            {submitting ? 'Creating account...' : 'Sign up'}
          </Button>
        </form>
        <p className="text-center text-sm text-[#455561]">Already have an account? <Link to="/login" className="text-[#1B4332] font-semibold hover:underline">Log in</Link></p>
      </Card>
    </div>
  );
}
